// Hook para obtener y gestionar el estado de Google Calendar
import { useState, useEffect, useCallback } from 'react';
import { googleCalendarService, type GoogleCalendarStatus } from '@/services/googleCalendarService';

interface UseGoogleCalendarStatusReturn {
  status: GoogleCalendarStatus | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  isConnecting: boolean;
  isDisconnecting: boolean;
}

export function useGoogleCalendarStatus(): UseGoogleCalendarStatusReturn {
  const [status, setStatus] = useState<GoogleCalendarStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await googleCalendarService.getStatus();
      setStatus(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Error al obtener estado de Google Calendar');
      setStatus({ connected: false });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const connect = useCallback(async () => {
    setIsConnecting(true);
    setError(null);
    try {
      const { url } = await googleCalendarService.getConnectUrl();
      if (!url) throw new Error('No se recibió URL de autorización');
      // Redirigir a Google OAuth — al volver, el callback procesará el code
      window.location.href = url;
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Error al iniciar conexión con Google');
      setIsConnecting(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    setIsDisconnecting(true);
    setError(null);
    try {
      await googleCalendarService.disconnect();
      setStatus({ connected: false });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Error al desconectar Google Calendar');
    } finally {
      setIsDisconnecting(false);
    }
  }, []);

  return {
    status,
    isLoading,
    error,
    refetch: fetchStatus,
    connect,
    disconnect,
    isConnecting,
    isDisconnecting,
  };
}
